import { JSX, ReactNode } from "react";

import { create } from "zustand";
import { devtools } from "zustand/middleware";
import { ComponentFactory } from "@/app/(components)/_components/ComponentFactory";
import { Components } from "@/app/(components)/_components/Components";
import { ModalType } from "@/app/(components)/generics/Modal/Modal";
import { hideCrispChat, showCrispChat } from "@/app/libs/CrispChat";
import { TKey } from "@/app/translations/TKey";
import { T } from "@/app/translations/Translations";
import { PublicInsoleModel } from "@/app/types/PublicInsoleModel";
import { PublicInsolePair } from "@/app/types/PublicInsolePair";
import { PublicPatient } from "@/app/types/PublicPatient";
import { PublicUser } from "@/app/types/PublicUser";
import { ZonePresetConfig } from "@/app/types/ZonePreset";

type OnSuccess = () => void;

export type ModalState = {
  isOpen: boolean;
  title: ReactNode;
  component: JSX.Element | null;
  footer: ReactNode | null;
  type: ModalType | null;
  closeWhenClickOutside: boolean;
  openModal(
    component: Components,
    title: ReactNode,
    props?: {},
    type?: ModalType,
    footer?: ReactNode,
    closeWhenClickOutside?: boolean,
  ): void;
  openSettings(lang: string, initialTab?: string): void;
  openAddUser(lang: string, onSuccess?: OnSuccess): void;
  openEditUser(lang: string, user: PublicUser, onSuccess?: OnSuccess): void;
  openAddPatient(lang: string, onSuccess?: OnSuccess): void;
  openEditPatient(lang: string, patient: PublicPatient, onSuccess?: OnSuccess): void;
  openAddInsolePair(lang: string, patient: PublicPatient, onSuccess?: OnSuccess): void;
  openEditInsolePair(lang: string, pair: PublicInsolePair, onSuccess?: OnSuccess): void;
  openPatientInfo(
    lang: string,
    patient: PublicPatient,
    onEdit?: (patient: PublicPatient) => void,
  ): void;
  openAddInsoleModel(lang: string, onSuccess?: OnSuccess): void;
  openEditInsoleModel(lang: string, model: PublicInsoleModel, onSuccess?: OnSuccess): void;
  openAddZone(lang: string): void;
  openSaveZonePreset(lang: string, config: ZonePresetConfig, defaultName: string): void;
  openExportInsole(lang: string): void;
  closeModal(): void;
};

export const ModalStore = create<ModalState>()(
  devtools((set, getState) => ({
    isOpen: false,
    title: "",
    component: null,
    footer: null,
    type: null,
    closeWhenClickOutside: true,

    openModal: (
      componentName: Components,
      title: ReactNode,
      props?: {},
      type: ModalType = ModalType.MEDIUM,
      footer: ReactNode = null,
      closeWhenClickOutside: boolean = true,
    ) => {
      document.body.style.overflow = "hidden";
      hideCrispChat();
      set({
        isOpen: true,
        component: ComponentFactory.create(componentName, props),
        title,
        type,
        footer,
        closeWhenClickOutside,
      });
    },

    openSettings: (lang: string, initialTab?: string) => {
      getState().openModal(
        Components.SETTINGS,
        T(TKey.SETTINGS, lang),
        { lang, initialTab },
        ModalType.MEDIUM,
      );
    },

    openAddUser: (lang: string, onSuccess?: OnSuccess) => {
      getState().openModal(
        Components.ADD_USER,
        T(TKey.ADD_USER, lang),
        { lang, onSuccess },
        ModalType.MEDIUM,
      );
    },

    openEditUser: (lang: string, user: PublicUser, onSuccess?: OnSuccess) => {
      getState().openModal(
        Components.EDIT_USER,
        T(TKey.EDIT_USER, lang),
        { lang, user, onSuccess },
        ModalType.MEDIUM,
      );
    },

    openAddPatient: (lang: string, onSuccess?: OnSuccess) => {
      getState().openModal(
        Components.ADD_PATIENT,
        T(TKey.ADD_PATIENT, lang),
        { lang, onSuccess },
        ModalType.MEDIUM,
      );
    },

    openEditPatient: (lang: string, patient: PublicPatient, onSuccess?: OnSuccess) => {
      getState().openModal(
        Components.EDIT_PATIENT,
        T(TKey.EDIT_PATIENT, lang),
        { lang, patient, onSuccess },
        ModalType.MEDIUM,
      );
    },

    openAddInsolePair: (lang: string, patient: PublicPatient, onSuccess?: OnSuccess) => {
      getState().openModal(
        Components.ADD_INSOLE_PAIR,
        T(TKey.ADD_INSOLE_PAIR, lang),
        { lang, patient, onSuccess },
        ModalType.MEDIUM,
      );
    },

    openEditInsolePair: (lang: string, pair: PublicInsolePair, onSuccess?: OnSuccess) => {
      getState().openModal(
        Components.EDIT_INSOLE_PAIR,
        T(TKey.EDIT_INSOLE_PAIR, lang),
        { lang, pair, onSuccess },
        ModalType.MEDIUM,
      );
    },

    openPatientInfo: (
      lang: string,
      patient: PublicPatient,
      onEdit?: (patient: PublicPatient) => void,
    ) => {
      getState().openModal(
        Components.PATIENT_INFO,
        T(TKey.PATIENT_DETAILS, lang),
        { lang, patient, onEdit },
        ModalType.MEDIUM,
      );
    },

    openAddInsoleModel: (lang: string, onSuccess?: OnSuccess) => {
      getState().openModal(
        Components.ADD_INSOLE_MODEL,
        T(TKey.ADD_INSOLE_MODEL, lang),
        { lang, onSuccess },
        ModalType.MEDIUM,
      );
    },

    openEditInsoleModel: (lang: string, model: PublicInsoleModel, onSuccess?: OnSuccess) => {
      getState().openModal(
        Components.EDIT_INSOLE_MODEL,
        T(TKey.EDIT_INSOLE_MODEL, lang),
        {
          lang,
          model,
          onSuccess: () => {
            onSuccess?.();
            getState().closeModal();
          },
        },
        ModalType.MEDIUM,
      );
    },

    openAddZone: (lang: string) => {
      getState().openModal(Components.ADD_ZONE, T(TKey.ADD_ZONE, lang), { lang }, ModalType.SMALL);
    },

    openSaveZonePreset: (lang: string, config: ZonePresetConfig, defaultName: string) => {
      getState().openModal(
        Components.SAVE_ZONE_PRESET,
        T(TKey.SAVE_AS_PRESET, lang),
        { lang, config, defaultName },
        ModalType.SMALL,
      );
    },

    openExportInsole: (lang: string) => {
      getState().openModal(
        Components.EXPORT_INSOLE,
        T(TKey.EXPORT, lang),
        { lang },
        ModalType.SMALL,
      );
    },

    closeModal: () => {
      document.body.style.overflow = "auto";
      showCrispChat();
      set({ isOpen: false, component: undefined, footer: null, title: "" });
    },
  })),
);
